import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Usuarios.module.css';
import UsuarioModal from './UsuarioModal';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../../firebase';
import { listar, atualizar, COLECOES } from '../../../services/firestore';

// ====================================================================
// CADASTRO DE USUÁRIOS — Bloco 3.6
// Conectado ao Firestore — coleção: usuarios
// (Conta Firebase Auth continua passo manual no console; envio de
//  email real fica para a fase de Functions, como o aviso já indica.)
// ====================================================================

function corDoAvatar(nome) {
  const cores = [
    { bg: '#f0e6fb', text: '#5B2A8C' },
    { bg: '#FCEBEB', text: '#A32D2D' },
    { bg: '#E1F5EE', text: '#0F6E56' },
    { bg: '#EEEDFE', text: '#534AB7' },
    { bg: '#FFF8E6', text: '#7A5200' },
    { bg: '#E6F1FB', text: '#185FA5' },
  ];
  const hash = (nome.charCodeAt(0) + (nome.charCodeAt(1) || 0)) % cores.length;
  return cores[hash];
}

function inicial(nome) {
  return (nome || '?').charAt(0).toUpperCase();
}

const PERFIL_CLASS = {
  Gestor: 'perfilGestor',
  Operador: 'perfilOperador',
  Visualizador: 'perfilVisualizador',
};

// Normaliza perfil vindo do banco (pode estar minúsculo: "gestor")
function normalizarPerfil(p) {
  if (!p) return 'Operador';
  const s = String(p).toLowerCase();
  if (s === 'gestor') return 'Gestor';
  if (s === 'operador') return 'Operador';
  if (s === 'visualizador') return 'Visualizador';
  return p;
}

function normalizarUsuario(u) {
  return {
    ...u,
    nome: u.nome || '',
    email: u.email || '',
    telefone: u.telefone || '',
    cargo: u.cargo || '',
    perfil: normalizarPerfil(u.perfil),
    contaAuthCriada: u.contaAuthCriada ?? false,
    ultimoAcesso: u.ultimoAcesso ?? null,
    ativo: u.ativo ?? true,
  };
}

export default function Usuarios() {
  const navigate = useNavigate();

  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [busca, setBusca] = useState('');
  const [filtroPerfil, setFiltroPerfil] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const dados = await listar(COLECOES.USUARIOS, { ordenarPor: 'nome' });
      setUsuarios(dados.map(normalizarUsuario));
    } catch (e) {
      setErro(e.message || 'Erro ao carregar usuários.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const usuariosFiltrados = useMemo(() => {
    return usuarios.filter((u) => {
      if (busca) {
        const q = busca.toLowerCase();
        const match =
          u.nome.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (filtroPerfil && u.perfil !== filtroPerfil) return false;
      if (filtroStatus === 'Ativos' && !u.ativo) return false;
      if (filtroStatus === 'Inativos' && u.ativo) return false;
      return true;
    });
  }, [usuarios, busca, filtroPerfil, filtroStatus]);

  const totalAtivos = usuarios.filter((u) => u.ativo).length;
  const totalInativos = usuarios.length - totalAtivos;

  function abrirNovo() {
    setUsuarioEditando(null);
    setModalAberto(true);
  }

  function abrirEditar(usuario) {
    setUsuarioEditando(usuario);
    setModalAberto(true);
  }

  function fecharModal() {
    setModalAberto(false);
    setUsuarioEditando(null);
  }

  async function salvar(dados) {
    setSalvando(true);
    setErro('');
    try {
      if (usuarioEditando) {
        const { senha: _s, ...dadosUpdate } = dados;
        await atualizar(COLECOES.USUARIOS, usuarioEditando.id, dadosUpdate);
      } else {
        const { senha, nome, email, perfil, cargo, telefone } = dados;
        const functions = getFunctions(undefined, 'southamerica-east1');
        const criarUsuario = httpsCallable(functions, 'criarUsuario');
        const result = await criarUsuario({ nome, email, senha, perfil, cargo, telefone });
        if (!result?.data?.ok) throw new Error('Falha ao criar usuário (resposta inesperada).');
      }
      fecharModal();
      await carregar();
    } catch (e) {
      setErro(e.message || 'Erro ao salvar o usuário.');
    } finally {
      setSalvando(false);
    }
  }

  async function toggleAtivo(id) {
    const alvo = usuarios.find((u) => u.id === id);
    if (!alvo) return;
    setErro('');
    setUsuarios((prev) =>
      prev.map((u) => (u.id === id ? { ...u, ativo: !u.ativo } : u))
    );
    try {
      await atualizar(COLECOES.USUARIOS, id, { ativo: !alvo.ativo });
    } catch (e) {
      setErro(e.message || 'Erro ao alterar o status do usuário.');
      setUsuarios((prev) =>
        prev.map((u) => (u.id === id ? { ...u, ativo: alvo.ativo } : u))
      );
    }
  }

  async function enviarEmailSenha(usuario) {
    if (!usuario.contaAuthCriada) {
      alert('A conta Firebase Auth ainda não foi criada para este usuário.\nEmail: ' + usuario.email);
      return;
    }
    try {
      await sendPasswordResetEmail(auth, usuario.email);
      alert('Email de redefinição de senha enviado para ' + usuario.email + '.');
    } catch (e) {
      alert('Erro ao enviar email: ' + (e.message || 'tente novamente.'));
    }
  }

  return (
    <>
      <div className={styles.topbar}>
        <div className={styles.pageTitle}>Cadastro de Usuários</div>
        <button className={styles.btnVoltar} onClick={() => navigate('/cadastros')}>
          ← Voltar para Cadastros
        </button>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div className={styles.avisoInfo}>
        <span className={styles.avisoInfoIcon}>ℹ️</span>
        <div className={styles.avisoInfoContent}>
          <strong>Como funciona o cadastro de usuários:</strong>
          <br />
          1. Preencha os dados e defina uma senha inicial.
          <br />
          2. Ao salvar, a conta Firebase Auth é criada automaticamente (via Cloud Function).
          <br />
          3. Clique em <strong>📧</strong> para enviar um email de redefinição de senha ao usuário.
        </div>
      </div>

      <div className={styles.toolbar}>
        <input
          className={styles.toolbarInput}
          placeholder="🔍 Buscar usuário, email..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select
          className={styles.toolbarSelect}
          value={filtroPerfil}
          onChange={(e) => setFiltroPerfil(e.target.value)}
        >
          <option value="">Todos os perfis</option>
          <option value="Gestor">Gestor</option>
          <option value="Operador">Operador</option>
          <option value="Visualizador">Visualizador</option>
        </select>
        <select
          className={styles.toolbarSelect}
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
        >
          <option value="">Todos os status</option>
          <option value="Ativos">Ativos</option>
          <option value="Inativos">Inativos</option>
        </select>
        <button className={styles.btnNovo} onClick={abrirNovo}>
          + Novo usuário
        </button>
      </div>

      <div className={styles.tabelaWrap}>
        <div className={styles.tabelaHead}>
          <span className={styles.tabelaCount}>
            {usuarios.length} usuários cadastrados ({totalAtivos} ativos · {totalInativos}{' '}
            {totalInativos === 1 ? 'inativo' : 'inativos'})
          </span>
          <span className={styles.tabelaHint}>Clique em ✏️ para editar</span>
        </div>

        <div className={styles.colHeader}>
          <div>Usuário</div>
          <div>Email / Contato</div>
          <div>Perfil</div>
          <div>Conta Auth</div>
          <div>Último acesso</div>
          <div></div>
        </div>

        {carregando ? (
          <div className={styles.semResultados}>Carregando usuários...</div>
        ) : usuariosFiltrados.length === 0 ? (
          <div className={styles.semResultados}>
            {usuarios.length === 0
              ? 'Nenhum usuário cadastrado ainda. Clique em "+ Novo usuário" para começar.'
              : 'Nenhum usuário encontrado com os filtros aplicados.'}
          </div>
        ) : (
          usuariosFiltrados.map((usuario) => {
            const corAvatar = corDoAvatar(usuario.nome);
            return (
              <div
                key={usuario.id}
                className={`${styles.userRow} ${!usuario.ativo ? styles.inativo : ''}`}
              >
                <div className={styles.userInfo}>
                  <div
                    className={styles.userAvatar}
                    style={{ background: corAvatar.bg, color: corAvatar.text }}
                  >
                    {inicial(usuario.nome)}
                  </div>
                  <div>
                    <div className={styles.userNome}>{usuario.nome}</div>
                    {usuario.cargo && (
                      <div className={styles.userCargo}>{usuario.cargo}</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className={styles.userEmail}>{usuario.email}</div>
                  <div className={styles.userTelefone}>{usuario.telefone || '—'}</div>
                </div>
                <div>
                  <span
                    className={`${styles.perfilTag} ${styles[PERFIL_CLASS[usuario.perfil]]}`}
                  >
                    {usuario.perfil}
                  </span>
                </div>
                <div>
                  {usuario.contaAuthCriada ? (
                    <span className={`${styles.authStatus} ${styles.authCriada}`}>
                      ✓ Conta criada
                    </span>
                  ) : (
                    <span className={`${styles.authStatus} ${styles.authPendente}`}>
                      ⏳ Pendente
                    </span>
                  )}
                </div>
                <div>
                  {usuario.ultimoAcesso ? (
                    <span className={styles.ultimoAcesso}>{usuario.ultimoAcesso}</span>
                  ) : (
                    <span className={`${styles.ultimoAcesso} ${styles.nunca}`}>
                      nunca acessou
                    </span>
                  )}
                </div>
                <div className={styles.acoes}>
                  <span
                    className={`${styles.statusTag} ${usuario.ativo ? styles.stAtivo : styles.stInativo}`}
                  >
                    {usuario.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                  <button
                    className={`${styles.btnAcao} ${styles.email}`}
                    title={
                      usuario.contaAuthCriada
                        ? 'Reenviar email de senha'
                        : 'Conta ainda não criada'
                    }
                    onClick={() => enviarEmailSenha(usuario)}
                    disabled={!usuario.contaAuthCriada}
                  >
                    📧
                  </button>
                  <button
                    className={`${styles.btnAcao} ${styles.editar}`}
                    title="Editar"
                    onClick={() => abrirEditar(usuario)}
                  >
                    ✏️
                  </button>
                  <button
                    className={`${styles.btnAcao} ${styles.inativar}`}
                    title={usuario.ativo ? 'Inativar' : 'Reativar'}
                    onClick={() => toggleAtivo(usuario.id)}
                  >
                    {usuario.ativo ? '🚫' : '✅'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {modalAberto && (
        <UsuarioModal
          usuario={usuarioEditando}
          onSalvar={salvar}
          onCancelar={fecharModal}
          salvando={salvando}
        />
      )}
    </>
  );
}